import {
  confirmSignIn as amplifyConfirmSignIn,
  ConfirmSignInOutput,
  fetchAuthSession,
  getCurrentUser,
  signIn as amplifySignIn,
  SignInOutput,  signInWithRedirect as amplifySignInWithRedirect,
  signOut as amplifySignOut} from "aws-amplify/auth";
import {Hub} from "aws-amplify/utils";
import React, {createContext, useContext, useEffect, useState} from "react";

import {Api} from "./Api";
import {useApi} from "./PlayMakersProvider";
import {SelfUser, UserCreateParentResponse, UserUpdateAvatarResponse} from "./types";
import {providers, ProvidersType} from "./utils/providers";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthContextType {
  FederatedListener: React.FC;
  userData: SelfUser | null;
  providerData: ProvidersType;
  isLoggedIn: boolean;

  signOut: () => Promise<void>;
  signIn: (username: string, password: string) => Promise<SignInOutput>;
  signInWithRedirect: (provider: string) => Promise<void>;

  redirectTwitterLogin: () => void;
  linkFederated: Api["linkFederated"];
  unlinkFederated: Api["unlinkFederated"];
  getSteamProfile: Api["getSteamProfile"];

  confirmSignIn: (newPassword: string) => Promise<ConfirmSignInOutput>;
  resendConfirmationCode: (email: string, username: string) => Promise<void>;

  signUp: Api["signUp"];
  signUpConfirmation: Api["signUpConfirmation"];

  sendForgotPassword: Api["sendPasswordResetCode"];
  passwordReset: Api["passwordReset"];

  updateUserAvatar: (avatar: File) => Promise<UserUpdateAvatarResponse>;
  createUserParent: (preferredUsername: string, provider: string) => Promise<UserCreateParentResponse>;
  refreshLoggedInUser: (existingUser?: SelfUser) => Promise<SelfUser | void>;
  getUserMetadata: () => Promise<any>;
}

interface AuthProviderProps {
  children: React.ReactNode;
}
export function AuthProvider({children}: AuthProviderProps) {

  const {
    signUp: signUpApi,
    signUpConfirmation: signUpConfirmationApi,
    getMe: getMeApi,
    resendConfirmationCode: resendConfirmationCodeApi,
    sendPasswordResetCode: sendPasswordResetCodeApi,
    passwordReset: passwordResetApi,
    updateAvatar: updateAvatarApi,
    createUserParent: createUserParentApi,
    linkFederated: linkFederatedApi,
    unlinkFederated: unlinkFederatedApi,
    redirectTwitterLogin: redirectTwitterLoginApi,
    getUserMetadata: getUserMetadataApi,
    getSteamProfile: getSteamProfileApi
  } = useApi();


  const [userData, setUserData] = useState<SelfUser | null>(null);

  const isLoggedIn = window.localStorage.getItem("amplify-authenticator-authState") === "signedIn";

  interface AuthPayload {
    event: string;
    data?: unknown;
  }
  const FederatedListener: React.FC = () => {
    useEffect(() => {
      const listener = ({payload}: { payload: AuthPayload }) => {
        switch (payload.event) {
        case "signInWithRedirect":
          setLoggedInUser();
          break;
        case "signInWithRedirect_failure":
          console.error("An error has occurred during the OAuth flow.");
          break;
        default:
          break;
        }
      };

      /* Add the listener. Call hubListenerCancelToken() to cancel the listener on unmount
         https://docs.amplify.aws/react/build-a-backend/auth/connect-your-frontend/
           listen-to-auth-events/#stop-listening-to-events
      */
      const hubListenerCancelToken = Hub.listen("auth", listener);

      return () => {
        hubListenerCancelToken();
      };
    }, []);

    return null;
  };

  // Check inconsistency with with userData and loggedInUser, force logout if necessary
  // Can happen eg if amplify has cached data and changes are applied on the backend
  async function checkCognitoUser() {
    let cognitoUser;
    try {
      cognitoUser = await getCurrentUser();
      if(userData && !cognitoUser || !userData && cognitoUser ||
      userData?.username !== cognitoUser.username || userData.userId !== cognitoUser.userId) {
        if(cognitoUser) {
          // amplify and userData out of sync- force logout
          await signOut();
        }
      }
    } catch (e) {
      if(userData || cognitoUser) {
        // amplify and userData out of sync- force logout
        await signOut();
      }
      throw e;
    }
  }

  async function setLoggedInUser(existingUser?: SelfUser) {
    let me;
    try {
      if(existingUser)
        me = existingUser;
      else
        me = await getMeApi();
      if(!me)
        return;

      const session = await fetchAuthSession();

      const accessToken = session?.tokens?.accessToken?.toString();
      if(accessToken) me.accessToken = accessToken;

      const idToken = session?.tokens?.idToken?.toString();
      if(idToken)
        me.idToken = idToken;

      setUserData(me);
      window.localStorage.setItem("amplify-authenticator-authState", "signedIn");
      return me;
    } catch (e) {
      console.error("error getting user", e);
      setUserData(null);
      throw e;
    }
  }

  async function refreshLoggedInUser(existingUser?: SelfUser): Promise<SelfUser | void> {
    try {
      let user;
      if(existingUser)
        user = await setLoggedInUser(existingUser);
      else {
        // need to wait a little bit for cognito updates to propagate
        await new Promise(resolve => setTimeout(resolve, 1000));
        user = await setLoggedInUser();
      }
      if(!user)
        console.error("Failed to refresh local user");
      return user;
    } catch (e) {
      console.error("Error refreshing local user", e);
      throw e;
    }
  }

  const signIn = async(username: string, password: string) => {
    try {

      if(!username || !password) throw new Error("Missing username or password");

      checkCognitoUser();
      const {isSignedIn, nextStep} = await amplifySignIn({password, username});

      if(nextStep.signInStep === "DONE" && isSignedIn)
        await setLoggedInUser();

      return {isSignedIn, nextStep};
    } catch (e) {
      console.error("Error signing in", e);
      throw e;
    }
  };

  async function getUserMetadata() {
    try {
      if(!userData?.idToken)
        return;
      return await getUserMetadataApi(userData.idToken);
    } catch (e) {
      console.error("Error getting metadata", e);
      throw e;
    }
  }


  async function confirmSignIn(newPassword: string) {
    const {isSignedIn, nextStep} = await amplifyConfirmSignIn({challengeResponse: newPassword});
    if(nextStep.signInStep === "DONE" && isSignedIn) setLoggedInUser();
    return {isSignedIn, nextStep};
  }

  async function passwordReset(username: string, code: string, newPassword: string) {
    signOut();
    return await passwordResetApi(username, code, newPassword);
  }

  async function signOut() {
    await amplifySignOut();

    window.localStorage.removeItem("amplify-authenticator-authState");
    setUserData(null);
  }

  async function updateUserAvatar(avatar: File) {
    const response = await updateAvatarApi(avatar);
    if(response?.url) {
      setUserData(user => {
        if(user)
          user.avatar = response.url;
        return user;
      });
    }
    return response;
  }

  async function signInWithRedirect(provider: string) {
    checkCognitoUser();
    const providerName = providers[provider].identityProvider;

    // @ts-expect-error - AuthProvider does not include our custom providers ex: "Twitter"
    await amplifySignInWithRedirect({provider: providerName});
  }

  async function handleOnLoad() {
    setLoggedInUser();
  }

  const getSteamProfile = async(): Promise<unknown> => {
    try {
      const response = await getSteamProfileApi();
      if(response)
        await refreshLoggedInUser();
      return response;
    } catch (error) {
      console.error("Error fetching steam profile", error);
      throw new Error("Error fetching steam profile", {cause: error});
    }
  };

  useEffect(() => {
    handleOnLoad();
  }, []);


  const auth: AuthContextType = {
    FederatedListener,
    confirmSignIn,
    createUserParent: createUserParentApi,
    getSteamProfile,
    getUserMetadata,
    isLoggedIn,
    linkFederated: linkFederatedApi,
    passwordReset,
    providerData: providers,
    redirectTwitterLogin: redirectTwitterLoginApi,
    refreshLoggedInUser,
    resendConfirmationCode: resendConfirmationCodeApi,
    sendForgotPassword: sendPasswordResetCodeApi,
    signIn,
    signInWithRedirect,
    signOut,
    signUp: signUpApi,
    signUpConfirmation: signUpConfirmationApi,
    unlinkFederated: unlinkFederatedApi,
    updateUserAvatar,
    userData
  };

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if(!context)
    throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
